import bind from 'bind-decorator';

import { App } from '../App';

import { AsyncFunction } from '@egr/xbox/utils/Types';
import { clearDebugFlag, showDebugFlag } from '@egr/xbox/utils/Debug';
import { emulateBackButtonPress } from '@egr/xbox/app-api/DebugActions';

export interface BackButtonRequirements extends App {
    backButtonManager: BackButtonManager;
}

export class BackButtonManager {
    protected actionStack: Array<AsyncFunction | BackButtonManager> = [];

    public get stackLength(): number {
        return this.actionStack.length;
    }

    public constructor(protected fallbackAction?: AsyncFunction<boolean>) {
        this.callLastAction = this.callLastAction.bind(this);
    }

    @bind
    public add(value: AsyncFunction | BackButtonManager): void {
        if (this.actionStack.length) {
            const lastEntry: AsyncFunction | BackButtonManager = this.actionStack[this.actionStack.length - 1];
            if (lastEntry instanceof BackButtonManager && lastEntry.stackLength === 0) {
                this.actionStack.length -= 1;
            }
        }

        this.actionStack.push(value);
    }

    @bind
    public clear(): void {
        this.actionStack.length = 0;
    }

    @bind
    public async callLastAction(): Promise<boolean> {
        if (this.actionStack.length === 0) {
            if (this.fallbackAction != null) {
                return this.fallbackAction();
            }

            return false;
        }

        const lastEntry: VoidFunction | BackButtonManager | undefined = this.actionStack[this.actionStack.length - 1];
        if (!(lastEntry instanceof BackButtonManager)) {
            this.actionStack.length--;

            if (lastEntry != null) {
                lastEntry();

                return true;
            }

            return false;
        }

        if (lastEntry.actionStack.length <= 1) {
            await lastEntry.callLastAction();
            this.removeLastAction();
            return true;
        }
        if (await lastEntry.callLastAction()) {
            return true;
        }

        this.actionStack.length--;

        return this.callLastAction();
    }

    @bind
    public removeLastAction(): boolean {
        if (this.actionStack.length > 0) {
            const lastEntry: VoidFunction | BackButtonManager | undefined = this.actionStack[this.actionStack.length - 1];
            if (!(lastEntry instanceof BackButtonManager)) {
                this.actionStack.length--;
            } else if (!lastEntry.removeLastAction()) {
                this.actionStack.length--;
            }
            return true;
        }

        return false;
    }

    public *debugActionCounter(): IterableIterator<number> {
        yield this.actionStack.length;

        if (this.actionStack.length !== 0) {
            const lastEntry: VoidFunction | BackButtonManager | undefined = this.actionStack[this.actionStack.length - 1];
            if (lastEntry instanceof BackButtonManager) {
                yield *lastEntry.debugActionCounter();
            }
        }
    }
}

export function enableBackButtonManagerDebugFlag(rootManager: BackButtonManager): void {
    const flagId: string = 'debug-back-button-manager';

    window.setInterval(
        (): void => {
            clearDebugFlag(flagId);

            showDebugFlag({
                key: flagId,
                value: `BackButtonActions: ${Array.from(rootManager.debugActionCounter()).join(',')}`,
                onClick: emulateBackButtonPress
            });
        },
        1000
    );
}